"use client";
import { useState, useEffect } from "react";
import { useParams } from "next/navigation";
import Link from "next/link";
import { listingApi } from "@/lib/api/listings";
import { getUserInfo } from "@/lib/api/client";
import { useI18n, getLocalizedField } from "@/i18n";
import { useLangHref } from "@/i18n/useLangHref";
import ChatModal from "../../account/ChatModal";
import LoginModal from "@/components/layout/LoginModal";
import "../../account/account.css";

const formatPrice = (price) => {
    if (price == null || isNaN(price)) return "—";
    const millions = (price / 1000000).toFixed(0);
    return `GNF ${millions}M`;
};

export default function DetailPage() {
    const { t, lang } = useI18n();
    const langHref = useLangHref();
    const { id } = useParams();
    const [listing, setListing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [mainImage, setMainImage] = useState(0);
    const [chatOpen, setChatOpen] = useState(false);
    const [loginOpen, setLoginOpen] = useState(false);
    const [similarList, setSimilarList] = useState([]);
    const [toast, setToast] = useState("");
    const [isFavorited, setIsFavorited] = useState(false);
    const [favLoading, setFavLoading] = useState(false);

    useEffect(() => {
        if (!id) return;
        setLoading(true);
        listingApi.listingDetail(id).then((res) => {
            const d = res.data || res;
            setListing(d);
            setIsFavorited(!!d?.isFavorite);
        }).catch(() => {
            setListing(null);
        }).finally(() => {
            setLoading(false);
        });
    }, [id]);

    useEffect(() => {
        if (!id) return;
        listingApi.similarListings(id).then((res) => {
            const arr = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : Array.isArray(res?.records) ? res.records : [];
            setSimilarList(arr);
        }).catch(() => {
            setSimilarList([]);
        });
    }, [id]);

  const getPlanName = (planType) => {
        const map = {
            bump: t('promote.planTypes.bump'),
            pin: t('promote.planTypes.pin'),
            homepage: t('promote.planTypes.homepage'),
        };
        return map[planType] ?? '';
    };

    const copyToClipboard = async (text) => {
        if (!text) return;
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                const ta = document.createElement("textarea");
                ta.value = text;
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
            }
            setToast(t("detail.copiedToClipboard"));
            setTimeout(() => setToast(""), 1800);
        } catch (_) {}
    };

    const toggleFavorite = async () => {
        if (favLoading || !listing) return;
        setFavLoading(true);
        try {
            const next = !isFavorited;
            setIsFavorited(next);
            await listingApi.toggleFavorite(listing.id);
            setToast(t(next ? "detail.favoriteAdded" : "detail.favoriteRemoved"));
            setTimeout(() => setToast(""), 1800);
        } catch (err) {
            setIsFavorited(!isFavorited); // 回滚
        } finally {
            setFavLoading(false);
        }
    };

    // Shimmer keyframes — only injected once per page
    const shimmerCss = (
        <style>{`
            @keyframes __skel_shimmer__ {
                0% { background-position: -600px 0; }
                100% { background-position: 600px 0; }
            }
            .skel-block {
                background: linear-gradient(90deg, #eef1ee 0px, #f7f9f7 300px, #eef1ee 600px);
                background-size: 1200px 100%;
                animation: __skel_shimmer__ 1.4s linear infinite;
                border-radius: 8px;
            }
        `}</style>
    );

    const skel = {
        block: (w, h, extra = {}) => (
            <div className="skel-block" style={{ width: w, height: h, ...extra }} />
        ),
    };

    if (loading) {
        return (
            <>
                {shimmerCss}
                <div className="container" style={{ padding: "24px 0 60px" }}>
                    {/* breadcrumb skeleton */}
                    <div style={{ display: "flex", gap: 16, marginBottom: 20, alignItems: "center" }}>
                        {skel.block(80, 14)}
                        {skel.block(10, 10, { borderRadius: "50%", width: 10, height: 10 })}
                        {skel.block(160, 14)}
                    </div>

                    <div className="detail-layout">
                        <main>
                            {/* gallery skeleton */}
                            <div className="gallery" style={{ filter: "none" }}>
                                <div className="gallery-main" style={{ padding: 0, overflow: "hidden" }}>
                                    <div className="skel-block" style={{ width: "100%", height: "100%", borderRadius: 0 }} />
                                </div>
                                <div className="gallery-thumbs">
                                    {[1,2,3,4].map(i => (
                                        <div key={i} className="gallery-thumb" style={{ padding: 0 }}>
                                            <div className="skel-block" style={{ width: "100%", height: "100%", borderRadius: 0 }} />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* title */}
                            <div style={{ display: "flex", gap: 12, margin: "28px 0 14px", alignItems: "center" }}>
                                {skel.block(420, 32, { borderRadius: 6 })}
                                {skel.block(80, 22)}
                            </div>

                            {/* meta row */}
                            <div style={{ display: "flex", gap: 24, alignItems: "center", marginBottom: 24 }}>
                                {skel.block(120, 14)}
                                {skel.block(110, 14)}
                                {skel.block(80, 14)}
                                {skel.block(100, 22)}
                                <div style={{ marginLeft: "auto" }}>{skel.block(90, 32, { borderRadius: 20 })}</div>
                            </div>

                            {/* specs section */}
                            <div className="detail-section" style={{ padding: 0, marginBottom: 24, border: "none" }}>
                                {skel.block(180, 20, { marginBottom: 16 })}
                                <div className="spec-grid">
                                    {Array.from({ length: 10 }).map((_, i) => (
                                        <div key={i} className="spec-item" style={{ border: "1px solid #f1f4f1", borderRadius: 10, padding: "14px 16px" }}>
                                            {skel.block(50, 12, { marginBottom: 6 })}
                                            {skel.block(90, 16)}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* description */}
                            <div className="detail-section" style={{ padding: 0, border: "none", marginBottom: 24 }}>
                                {skel.block(140, 20, { marginBottom: 16 })}
                                {skel.block("100%", 14, { marginBottom: 10 })}
                                {skel.block("94%", 14, { marginBottom: 10 })}
                                {skel.block("88%", 14, { marginBottom: 10 })}
                                {skel.block("70%", 14)}
                            </div>
                        </main>

                        <aside className="sticky-sidebar">
                            {/* price card */}
                            <div className="price-card" style={{ padding: 20 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 18 }}>
                                    {skel.block(180, 34, { borderRadius: 6 })}
                                    {skel.block(80, 22)}
                                </div>
                                {skel.block("100%", 46, { borderRadius: 10, marginBottom: 10 })}
                                {skel.block("100%", 46, { borderRadius: 10, marginBottom: 10 })}
                                {skel.block("100%", 40, { borderRadius: 10 })}
                            </div>

                            {/* seller card */}
                            <div className="seller-card" style={{ padding: 20, marginTop: 16 }}>
                                <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 18 }}>
                                    {skel.block(44, 44, { borderRadius: "50%" })}
                                    <div style={{ flex: 1 }}>
                                        {skel.block(140, 16, { marginBottom: 6 })}
                                        {skel.block(90, 12)}
                                    </div>
                                </div>
                                <div style={{ height: 1, background: "#f1f4f1", margin: "14px 0" }} />
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                                    {skel.block(60, 12)}{skel.block(50, 12)}
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                                    {skel.block(70, 12)}{skel.block(60, 12)}
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between" }}>
                                    {skel.block(50, 12)}{skel.block(40, 12)}
                                </div>
                            </div>

                            {/* safety card */}
                            <div className="card mt-4" style={{ padding: 20 }}>
                                <div style={{ display: "flex", gap: 12, marginBottom: 14, alignItems: "center" }}>
                                    {skel.block(40, 40, { borderRadius: 10 })}
                                    {skel.block(100, 16)}
                                </div>
                                {skel.block("92%", 12, { marginBottom: 8 })}
                                {skel.block("88%", 12, { marginBottom: 8 })}
                                {skel.block("90%", 12, { marginBottom: 8 })}
                                {skel.block("85%", 12)}
                            </div>
                        </aside>
                    </div>
                </div>
            </>
        );
    }

    if (!listing) {
        return (
            <div className="container" style={{ padding: "80px 0", textAlign: "center" }}>
                <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>404</div>
                <div style={{ color: "var(--color-text-muted)" }}>{t("common.notFound")}</div>
                <Link href={langHref("/")} className="btn btn-primary" style={{ marginTop: 20, display: "inline-flex" }}>{t("nav.home")}</Link>
            </div>
        );
    }

    const images = listing.images?.length > 0 ? listing.images : (listing.coverUrl ? [listing.coverUrl] : []);
    const title = listing.title || "";
    const isNegotiable = listing.priceNegotiable === 1;
    const priceDisplay = isNegotiable ? t("car.negotiable") : formatPrice(listing.priceGnf);
    const currentUser = getUserInfo();
    const isOwner = currentUser && listing.userId && currentUser.id === listing.userId;
    
    return (
        <>
        <div className="container">
            {/* 面包屑 */}
            <div className="account-breadcrumb" style={{ display: "flex", alignItems: "center", gap: 6, padding: "20px 0 16px", whiteSpace: "nowrap", overflow: "hidden", fontSize: 13, color: "#6B7280" }}>
                <Link href={langHref("/")} style={{ flexShrink: 0 }}>{t("nav.home")}</Link>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14" style={{ flexShrink: 0 }}>
                    <path d="m9 18 6-6-6-6" />
                </svg>
                <Link href={langHref("/search")} style={{ flexShrink: 0 }}>{t("search.title")}</Link>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14" style={{ flexShrink: 0 }}>
                    <path d="m9 18 6-6-6-6" />
                </svg>
                <span className="current" style={{ color: "var(--color-text)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", minWidth: 0, flexShrink: 1 }}>{title}</span>
            </div>

            <div className="detail-layout">
                <main>
                    {/* 图片画廊 */}
                    <div className="gallery">
                        <div className="gallery-main">
                            <img
                                src={images[mainImage] || "https://via.placeholder.com/800x500?text=No+Image"}
                                alt={title}
                            />
                            <div className="gallery-badge">
                                {listing.isVerified === 1 && <span className="tag tag-brand">{t("car.verified")}</span>}
                                {listing.isFeatured === 1 && <span className="tag tag-success">{t("car.promoted")}</span>}
                            </div>
                        </div>
                        {images.length > 1 && (
                            <div className="gallery-thumbs">
                                {images.map((img, i) => (
                                    <div
                                        key={i}
                                        className={`gallery-thumb ${i === mainImage ? "active" : ""}`}
                                        onClick={() => setMainImage(i)}
                                    >
                                        <img src={img} alt={`${title} ${i + 1}`} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* 标题 */}
                    <div className="flex items-center gap-3 mb-4">
                        <h1 style={{ fontSize: 30, margin: 0 }}>{title}</h1>
                        {listing.isVerified === 1 && <span className="tag tag-brand">{t("car.verified")}</span>}
                    </div>
                    <div className="flex gap-6 mb-6 text-secondary" style={{ color: "var(--color-text-secondary)", alignItems: "center" }}>
                        {listing.cityName && <span>📍 {getLocalizedField(listing, "cityName", lang)}</span>}
                        {listing.createTime && <span>📅 {new Date(listing.createTime).toLocaleDateString("en-CA")}</span>}
                        <span>👁 {listing.viewCount ?? 0} views</span>
                        {(() => {
                            const st = Number(listing.status ?? 0);
                            const palette = {
                                0: { bg: "#F3F4F6", fg: "#6B7280",  dot: "#9CA3AF" }, // 草稿 灰
                                1: { bg: "#FFF7ED", fg: "#B45309",  dot: "#F59E0B" }, // 待审核 琥珀
                                2: { bg: "#F5FCE9", fg: "#3DA005",  dot: "#53c906" }, // 在售 品牌绿
                                3: { bg: "#E0E7FF", fg: "#3730A3",  dot: "#6366F1" }, // 已售 靛蓝
                                4: { bg: "#FEF2F2", fg: "#B91C1C",  dot: "#EF4444" }, // 已下架 红
                                5: { bg: "#F9FAFB", fg: "#9CA3AF",  dot: "#9CA3AF" }, // 已过期 暗灰
                                6: { bg: "#FEF2F2", fg: "#B91C1C",  dot: "#DC2626" }, // 驳回 红
                            };
                            const c = palette[st] || palette[0];
                            return (
                                <span style={{
                                    display: "inline-flex", alignItems: "center", gap: 6,
                                    padding: "4px 12px", borderRadius: 999,
                                    background: c.bg, color: c.fg,
                                    fontSize: 12, fontWeight: 600, letterSpacing: 0.1,
                                }}>
                                    <span style={{
                                        width: 7, height: 7, borderRadius: "50%",
                                        background: c.dot, display: "inline-block",
                                    }} />
                                    {t("detail.status" + st)}
                                </span>
                            );
                        })()}
                        <button
                            onClick={toggleFavorite}
                            disabled={favLoading}
                            style={{
                                marginLeft: "auto",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "6px 14px",
                                borderRadius: 20,
                                border: `1px solid ${isFavorited ? "#ff4d4f" : "var(--color-border)"}`,
                                background: isFavorited ? "#fff1f0" : "transparent",
                                color: isFavorited ? "#ff4d4f" : "var(--color-text-secondary)",
                                cursor: favLoading ? "not-allowed" : "pointer",
                                fontSize: 13,
                                fontWeight: 500,
                                transition: "all .18s",
                            }}
                            onMouseEnter={(e) => { if(!favLoading) e.currentTarget.style.background = isFavorited ? "#ff4d4f" : "var(--color-bg-hover)"; if(!favLoading) e.currentTarget.style.color = isFavorited ? "#fff" : "var(--color-text)"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = isFavorited ? "#fff1f0" : "transparent"; e.currentTarget.style.color = isFavorited ? "#ff4d4f" : "var(--color-text-secondary)"; }}
                        >
                            {isFavorited ? (
                                <svg viewBox="0 0 24 24" fill="currentColor" width={16} height={16}>
                                    <path d="M12 21s-7-4.5-9.5-9.5C1 8 4 4 7.5 4c2 0 3.5 1 4.5 2.5h1C13 5 14.5 4 16.5 4 20 4 23 8 21.5 11.5 19 16.5 12 21 12 21z"/>
                                </svg>
                            ) : (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={16} height={16}>
                                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                                </svg>
                            )}
                            {isFavorited ? t("detail.unfavorite") : t("detail.favorite")}
                        </button>
                    </div>

                    {/* 车辆规格 */}
                    <div className="detail-section">
                        <div className="detail-section-title">{t("detail.specs")}</div>
                            <div className="spec-grid">
                                <div className="spec-item">
                                <div className="spec-label">{t("car.year")}</div>
                                <div className="spec-value">{ !isNaN(listing.carYear) && listing.carYear > 0 ? listing.carYear : t("publish.yearNA")}
                                </div>
                            </div>
                            {listing.mileage != null && (
                                <div className="spec-item">
                                    <div className="spec-label">{t("detail.mileage")}</div>
                                    <div className="spec-value">{ !isNaN(listing.mileage) && listing.mileage > 0 ? `${listing.mileage.toLocaleString()} ${t("car.km")}` : t("publish.mileageNA") }</div>
                                </div>
                            )}
                            {listing.fuelType && (
                                <div className="spec-item">
                                    <div className="spec-label">{t("detail.fuel")}</div>
                                    <div className="spec-value">
                                        {t(`car.${listing.fuelType}`) || listing.fuelType}
                                        {listing.fuelType === "electric"
                                            ? (listing.batteryCapacity ? ` · ${listing.batteryCapacity}kWh` : "")
                                            : (listing.engineSize ? ` · ${listing.engineSize}L` : "")}
                                    </div>
                                </div>
                            )}
                            {listing.transmission && (
                                <div className="spec-item">
                                    <div className="spec-label">{t("detail.transmission")}</div>
                                    <div className="spec-value">{t(`car.${listing.transmission}`) || listing.transmission}</div>
                                </div>
                            )}
                            {listing.bodyType && (
                                <div className="spec-item">
                                    <div className="spec-label">{t("detail.bodyType")}</div>
                                    <div className="spec-value">{getLocalizedField(listing, "bodyTypeName", lang)}</div>
                                </div>
                            )}
                            {listing.condition && (
                                <div className="spec-item">
                                    <div className="spec-label">{t("detail.condition")}</div>
                                    <div className="spec-value">{t(`car.${listing.condition}`) || listing.condition}</div>
                                </div>
                            )}
                            {listing.doors && (
                                <div className="spec-item">
                                    <div className="spec-label">{t("detail.doors")}</div>
                                    <div className="spec-value">{listing.doors}</div>
                                </div>
                            )}
                            {listing.seats && (
                                <div className="spec-item">
                                    <div className="spec-label">{t("detail.seats")}</div>
                                    <div className="spec-value">{listing.seats}</div>
                                </div>
                            )}
                            {listing.color && (
                                <div className="spec-item">
                                    <div className="spec-label">{t("detail.color")}</div>
                                    <div className="spec-value">{listing.color}</div>
                                </div>
                            )}
                            {listing.vin && (
                                <div className="spec-item">
                                    <div className="spec-label">{t("detail.vin")}</div>
                                    <div className="spec-value" style={{ fontFamily: "monospace" }}>{listing.vin}</div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 车辆描述 */}
                    {listing.description && (
                        <div className="detail-section">
                            <div className="detail-section-title">{t("detail.banner")}</div>
                            <p className="description-text" style={{ whiteSpace: "pre-wrap" }}>{listing.description}</p>
                        </div>
                    )}

                    {/* 配件信息 */}
                    {listing.partType && (
                        <div className="detail-section">
                            <div className="detail-section-title">{t("detail.partType")}</div>
                            <div className="spec-grid">
                                <div className="spec-item">
                                    <div className="spec-label">{t("detail.partType")}</div>
                                    <div className="spec-value">{listing.partType === "original" ? t("detail.partOriginal") : listing.partType === "aftermarket" ? t("detail.partAftermarket") : listing.partType === "salvage" ? t("detail.partSalvage") : listing.partType}</div>
                                </div>
                                {listing.oeNumber && (
                                    <div className="spec-item">
                                        <div className="spec-label">{t("detail.oeNumber")}</div>
                                        <div className="spec-value">{listing.oeNumber}</div>
                                    </div>
                                )}
                                {listing.fitModels && (
                                    <div className="spec-item">
                                        <div className="spec-label">{t("detail.fitModels")}</div>
                                        <div className="spec-value">{listing.fitModels}</div>
                                    </div>
                                )}
                                {listing.warranty && (
                                    <div className="spec-item">
                                        <div className="spec-label">{t("detail.warranty")}</div>
                                        <div className="spec-value">{listing.warranty}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* 车辆位置 */}
                    {listing.cityName && (
                        <div className="detail-section">
                            <div className="detail-section-title">{t("detail.location")}</div>
                            <div style={{
                                height: 240,
                                borderRadius: "var(--radius)",
                                background: "linear-gradient(135deg, #E8F7D8 0%, #D4EFB4 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                position: "relative",
                                overflow: "hidden",
                            }}>
                                <svg width="100%" height="100%" viewBox="0 0 800 300" preserveAspectRatio="xMidYMid slice">
                                    <defs>
                                        <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                                            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(30, 64, 175, 0.1)" strokeWidth="1" />
                                        </pattern>
                                    </defs>
                                    <rect width="800" height="300" fill="url(#grid)" />
                                    <path d="M 100 150 Q 250 80, 400 150 T 700 150" stroke="#53c906" strokeWidth="3" fill="none" strokeDasharray="8 4" />
                                    <circle cx="400" cy="150" r="14" fill="#53c906" />
                                    <circle cx="400" cy="150" r="28" fill="none" stroke="#53c906" strokeWidth="2" opacity="0.4" />
                                </svg>
                                <div style={{
                                    position: "absolute",
                                    bottom: 16,
                                    left: 16,
                                    background: "white",
                                    padding: "10px 16px",
                                    borderRadius: "var(--radius)",
                                    boxShadow: "var(--shadow-md)",
                                    display: "flex",
                                    gap: 8,
                                    alignItems: "center",
                                }}>
                                    <span style={{ fontSize: 18 }}>📍</span>
                                    <div>
                                        <div style={{ fontWeight: 700 }}>{ getLocalizedField(listing, "cityName", lang)}</div>
                                        <div style={{ fontSize: 13, color: "var(--color-text-muted)" }}>
                                            {getLocalizedField(listing, "brandName", lang)} {getLocalizedField(listing, "modelName", lang)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 相似车辆推荐 */}
                    {similarList.length > 0 && (
                        <div style={{ marginTop: 40 }}>
                            <div style={{ fontSize: 20, fontWeight: 800, color: "#111827", marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
                                <span style={{ width: 3, height: 20, background: "#53c906", borderRadius: 2 }} />
                                {t("detail.similarTitle")}
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
                                {similarList.slice(0, 6).map((item) => {
                                    const nego = Number(item.priceNegotiable) === 1;
                                    const priceText = nego
                                        ? t("detail.negotiable")
                                        : (item.priceGnf != null && Number(item.priceGnf) > 0
                                            ? `GNF ${(Number(item.priceGnf) / 1000000).toFixed(0)}M`
                                            : item.priceUsd != null ? `$${item.priceUsd}` : t("detail.negotiable"));
                                    const cityLabel = getLocalizedField(item, "cityName", lang) || "";
                                    const engineLabel = item.engineSize ? `${item.engineSize}` : "";
                                    return (
                                        <Link
                                            key={item.id}
                                            href={langHref(`/detail/${item.id}`)}
                                            style={{
                                                background: "#fff",
                                                borderRadius: 12,
                                                border: "1px solid #ECEEEC",
                                                overflow: "hidden",
                                                textDecoration: "none",
                                                display: "flex",
                                                flexDirection: "column",
                                                transition: "transform 0.15s ease, box-shadow 0.15s ease",
                                                cursor: "pointer",
                                            }}
                                            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.08)"; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
                                        >
                                            <div style={{ aspectRatio: "16/10", background: "#F3F4F6", overflow: "hidden" }}>
                                                {item.coverUrl ? (
                                                    <img
                                                        src={item.coverUrl}
                                                        alt={item.title}
                                                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                    />
                                                ) : (
                                                    <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#D1D5DB", fontSize: 40 }}>🚗</div>
                                                )}
                                            </div>
                                            <div style={{ padding: "14px 16px 16px", flex: 1 }}>
                                                <div style={{ fontSize: 15, fontWeight: 700, color: "#111827", marginBottom: 6, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {item.title || `${getLocalizedField(item, "brandName", lang) || ""} ${getLocalizedField(item, "modelName", lang) || ""}`.trim()}
                                                </div>
                                                <div style={{ fontSize: 13, color: "#6B7280", display: "flex", alignItems: "center", gap: 4, marginBottom: 10 }}>
                                                    <span>📍</span>
                                                    <span>{cityLabel}</span>
                                                    {cityLabel && engineLabel && <span style={{ opacity: 0.5 }}>·</span>}
                                                    {engineLabel && <span>{engineLabel}</span>}
                                                </div>
                                                <div style={{
                                                    fontSize: 16,
                                                    fontWeight: 700,
                                                    color: nego ? "#6B7280" : "#2F9E44",
                                                    letterSpacing: "-0.01em",
                                                }}>
                                                    {priceText}
                                                </div>
                                            </div>
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </main>

                <aside className="sticky-sidebar">
                    {/* 价格卡片 */}
                    <div className="price-card">
                        <div className="price-card-top">
                            <div>
                                <div className={`price-amount ${isNegotiable ? "negotiable" : ""}`}>
                                    {priceDisplay}
                                </div>
                                {listing.priceUsd != null && <div className="price-meta">≈ ${listing.priceUsd.toLocaleString()} USD</div>}
                            </div>
                            <span className={`table-status ${listing.status === 2 ? "active" : ""}`}>
                                {listing.status === 2 ? t("detail.listed") : listing.status === 3 ? t("detail.sold") : listing.status === 0 ? t("detail.draft") : listing.status === 1 ? t("detail.pending") : listing.status === 4 ? t("detail.offline") : listing.status === 5 ? t("detail.expired") : listing.status === 6 ? t("detail.rejected") : "—"}
                            </span>
                        </div>
                        <div className="contact-buttons">
                            {listing?.whatsapp && (
                                <button className="btn btn-accent btn-lg btn-block" onClick={() => copyToClipboard(listing.whatsapp)}>
                                    <svg viewBox="0 0 24 24" fill="currentColor" width={20} height={20}>
                                        <path d="M17.6 6.32A7.85 7.85 0 0 0 12.04 4c-4.35 0-7.89 3.54-7.89 7.89 0 1.39.36 2.74 1.05 3.94L4 20l4.31-1.13a7.87 7.87 0 0 0 3.73.94h.01c4.35 0 7.88-3.54 7.88-7.89 0-2.11-.82-4.09-2.32-5.59zm-5.55 12.21h-.01a6.55 6.55 0 0 1-3.33-.91l-.24-.14-2.56.67.68-2.49-.16-.25a6.51 6.51 0 0 1-1-3.49c0-3.6 2.94-6.54 6.55-6.54 1.75 0 3.39.68 4.63 1.92a6.5 6.5 0 0 1 1.92 4.62c0 3.6-2.94 6.54-6.55 6.54zm3.59-4.89c-.2-.1-1.17-.58-1.35-.64-.18-.07-.31-.1-.45.1-.13.2-.51.64-.62.77-.12.13-.23.15-.43.05-.2-.1-.85-.31-1.61-.99-.6-.53-1-1.18-1.12-1.38-.12-.2-.01-.31.09-.41.09-.09.2-.23.3-.35.1-.12.13-.2.2-.33.07-.13.03-.25-.02-.35-.05-.1-.45-1.08-.61-1.47-.16-.39-.33-.34-.45-.34h-.38c-.13 0-.34.05-.51.25-.18.2-.67.65-.67 1.59s.69 1.85.78 1.97c.1.13 1.34 2.05 3.25 2.87.45.2.81.31 1.08.4.45.14.87.12 1.19.07.36-.05 1.17-.48 1.33-.94.17-.46.17-.86.12-.94-.05-.08-.18-.13-.38-.23z" />
                                    </svg>
                                    {listing.whatsapp}
                                </button>
                            )}
                            {listing?.contactPhone && (
                                <button className="btn btn-primary btn-lg btn-block" onClick={() => copyToClipboard(listing.contactPhone)}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={20} height={20}>
                                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                                    </svg>
                                    {listing.contactPhone}
                                </button>
                            )}
                            {listing?.email && (
                                <button
                                  className="btn btn-ghost btn-block"
                                  style={{ color: "var(--color-text-primary)", background: "#f3f4f6", border: "1px solid #e5e7eb", backdropFilter: "none" }}
                                  onClick={() => copyToClipboard(listing.email)}
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={18} height={18}>
                                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                        <polyline points="22,6 12,13 2,6" />
                                    </svg>
                                    {listing.email}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* t("detail.seller")卡片 */}
                    <div className="seller-card">
                        <div className="seller-header">
                            <div className="seller-avatar">
                                {(listing.contactName || "D").charAt(0).toUpperCase()}
                            </div>
                            <div className="seller-info">
                                <div className="seller-name">{listing.contactName || t("detail.seller")}</div>
                                <div className="seller-meta">
                                    {listing.isVerified === 1 && <span className="seller-badge">✓ {t("car.verified")}</span>}
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col gap-2 text-sm">
                            <div className="flex justify-between">
                                    <span className="text-muted" style={{ color: "var(--color-text-muted)" }}>{t("detail.carYear")}</span>
                                    <span className="font-semibold">{listing.carYear == 0 || listing.carYear == null 
                                    || listing.carYear === '' || isNaN(Number(listing.carYear)) ? t("publish.yearNA") : listing.carYear}</span>
                                </div>
                            <div className="flex justify-between">
                                <span className="text-muted" style={{ color: "var(--color-text-muted)" }}>{t("detail.postedOn")}</span>
                                <span className="font-semibold">{listing.createTime ? new Date(listing.createTime).toLocaleDateString("en-CA") : "—"}</span>
                            </div>
                            {listing.viewCount != null && (
                                <div className="flex justify-between">
                                    <span className="text-muted" style={{ color: "var(--color-text-muted)" }}>{t("detail.views")}</span>
                                    <span className="font-semibold">{listing.viewCount}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 推广横幅 */}
                    {listing.isFeatured === 1 && (
                        <div className="promo-banner">
                            <div className="promo-banner-content">
                                <div className="promo-banner-title">🚀 {t("detail.sellerPromoTitle")}</div>
                                <div className="promo-banner-desc">
                                    {t("detail.sellerPromoDesc", { plan: getPlanName(listing.planType) })}
                                </div>
                                <Link href={langHref("/promote")} className="btn btn-lg" style={{ background: "white", color: "#53c906" }}>
                                    {t("detail.sellerPromoCta")}
                                </Link>
                            </div>
                        </div>
                    )}

                    {/* 安全提示 */}
                    <div className="card mt-4">
                        <div className="card-body">
                            <div className="flex items-center gap-3 mb-3">
                                <div style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 10,
                                    background: "var(--color-info-light)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 20,
                                }}>
                                    🛡️
                                </div>
                                <div style={{ fontWeight: 700 }}>{t("detail.safety")}</div>
                            </div>
                            <ul style={{ listStyle: "none", padding: 0, fontSize: 13, color: "var(--color-text-secondary)", lineHeight: 1.8 }}>
                                <li>· {t("detail.safety1")}</li>
                                <li>· {t("detail.safety2")}</li>
                                <li>· {t("detail.safety3")}</li>
                                <li>· {t("detail.safety4")}</li>
                            </ul>
                        </div>
                    </div>
                </aside>
            </div>

            {!isOwner && (
                <>
                    {/* 浮动询价按钮 */}
                    <button className="inquiry-fab" onClick={() => {
                        if (!currentUser) {
                            setLoginOpen(true);
                        } else {
                            setChatOpen(true);
                        }
                    }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                        </svg>
                        <span>{t("detail.quotationButton")}</span>
                    </button>

                    {/* 在线询价弹窗（改为立即回复聊天窗口） */}
                    {currentUser && (
                        <ChatModal
                            open={chatOpen}
                            onClose={() => setChatOpen(false)}
                            otherUserId={listing.userId}
                            otherName={listing.dealerName || t("detail.seller")}
                            otherAvatar=""
                            listingId={Number(id)}
                            listingTitle={listing.title || ""}
                            initialMessage=""
                            position="aboveBtn"
                        />
                    )}

                    {/* 未登录时弹出登录框 */}
                    <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />
                </>
            )}
        </div>

        {toast && (
            <div style={{
                position: "fixed",
                top: 24,
                left: "50%",
                transform: "translateX(-50%)",
                background: "#000",
                color: "#fff",
                padding: "10px 20px",
                borderRadius: 8,
                fontSize: 14,
                zIndex: 9999,
                boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
                animation: "fadeIn .2s ease",
            }}>{toast}</div>
        )}
        </>
    );
}
